import { useEffect, useMemo, useState } from "react";
import { FaFilePdf, FaInfoCircle } from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldSwitch, FieldFile } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";

const EMPTY = { title: "", description: "", author: "", groupId: "", isFavorite: false };
const MAX_GROUP_FILES = 20;
const valuesFrom = (pdf, initialGroupId = "") => pdf ? { title: pdf.title ?? "", description: pdf.description ?? "", author: pdf.author ?? "", groupId: pdf.groupId ?? "", isFavorite: !!pdf.isFavorite } : { ...EMPTY, groupId: initialGroupId || "" };
const titleFromFile = (name = "") => String(name).replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

export const LibraryPdfModal = ({ isOpen, onClose, document = null, groups = [], initialGroupId = "", onSave, isLoading = false }) => {
    const [values, setValues] = useState(EMPTY);
    const [files, setFiles] = useState([]);

    useEffect(() => {
        if (!isOpen) return;
        setValues(valuesFrom(document, initialGroupId));
        setFiles([]);
    }, [isOpen, document, initialGroupId]);

    const edit = !!document?.id;
    const batchMode = !edit && !!values.groupId;
    const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(valuesFrom(document, initialGroupId)) || files.length > 0, [values, document, initialGroupId, files]);
    const groupOptions = useMemo(() => [{ value: "", label: "Sin grupo" }, ...groups.map((g) => ({ value: g.id, label: g.name }))], [groups]);
    const field = (name) => ({ id: name, name, value: values[name] ?? "", onChange: (event) => setValues((current) => ({ ...current, [name]: event.target.value })) });

    const changeFiles = (next) => {
        const selected = batchMode ? next.slice(0, MAX_GROUP_FILES) : next.slice(-1);
        setFiles(selected);
        if (!edit && selected.length === 1 && !values.title.trim()) {
            setValues((current) => ({ ...current, title: titleFromFile(selected[0].name) }));
        }
    };

    const changeGroup = (event) => {
        const groupId = event.target.value || "";
        setValues((current) => ({ ...current, groupId }));
        if (!edit && !groupId && files.length > 1) {
            const first = files[0];
            setFiles(first ? [first] : []);
            if (first && !values.title.trim()) setValues((current) => ({ ...current, title: titleFromFile(first.name) }));
        }
    };

    const submit = async () => {
        if (edit && !values.title.trim()) return;
        if (!edit && files.length === 0) return;
        if (!edit && !values.groupId && files.length !== 1) return;

        const payload = {
            title: values.title.trim(),
            description: values.description.trim(),
            author: values.author.trim(),
            groupId: values.groupId || null,
            isFavorite: !!values.isFavorite,
        };

        const result = await onSave?.(payload, edit ? null : files);
        if (result === false) return;

        if (result && Array.isArray(result.failedFiles) && result.failedFiles.length > 0) {
            setFiles(result.failedFiles);
            if (result.failedFiles.length === 1) {
                setValues((current) => ({ ...current, title: current.title.trim() || titleFromFile(result.failedFiles[0].name) }));
            }
            return;
        }

        onClose?.();
    };

    const canSubmit = edit ? !!values.title.trim() : files.length > 0 && (values.groupId ? true : files.length === 1);
    const submitLabel = edit ? "Guardar cambios" : files.length > 1 ? `Subir ${files.length} PDFs` : "Subir PDF";

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="xl" isDirty={dirty}
            title={edit ? "Editar PDF" : "Subir PDF"}
            description={edit ? "Actualizá los datos o mové el documento a otro grupo." : "La biblioteca acepta exclusivamente archivos PDF."}
            footer={<><AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton><AppButton onClick={submit} isLoading={isLoading} isDisabled={!canSubmit}>{submitLabel}</AppButton></>}>
            <div className="LStudio__library_modal_stack">
                {!edit && <div className="LStudio__library_helpbox"><FaInfoCircle /><div><strong>Solo PDF real</strong><p>El backend valida extensión, MIME y firma %PDF. Un archivo renombrado a .pdf será rechazado.</p></div></div>}

                {!edit && (
                    <FormGrid columns={2}>
                        <FieldSelect id="groupId" name="groupId" value={values.groupId} onChange={changeGroup}
                            label="Grupo de destino" options={groupOptions} placeholder="Sin grupo" isSearchable={groups.length >= 8} span={2}
                            hint="Con un grupo seleccionado podés cargar varios PDFs juntos. Sin grupo, la carga es de un PDF por vez." />
                    </FormGrid>
                )}

                {!edit && <FieldFile id="library_pdf" label={batchMode ? "Archivos PDF" : "Archivo PDF"} files={files} onChange={changeFiles}
                    accept={{ "application/pdf": [".pdf"] }} maxFiles={batchMode ? MAX_GROUP_FILES : 1} maxSize={52428800}
                    dropText={batchMode ? "Arrastrá uno o varios PDFs o hacé clic para elegirlos" : "Arrastrá un PDF o hacé clic para elegirlo"}
                    isRequired span={2}
                    hint={batchMode ? `Hasta ${MAX_GROUP_FILES} PDFs por carga, máximo 50 MB cada uno.` : "Un archivo por vez. Máximo 50 MB."} />}

                {!edit && batchMode && files.length > 1 && (
                    <div className="LStudio__library_helpbox LStudio__library_helpbox--batch"><FaInfoCircle /><div><strong>Carga múltiple activa</strong><p>Cada PDF usará el nombre de su archivo como título. Autor, descripción y favorito se aplicarán a todos los documentos seleccionados.</p></div></div>
                )}

                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="isFavorite" name="isFavorite" label="Favorito" text="Destacar en la biblioteca"
                        isChecked={!!values.isFavorite}
                        onChange={(event) => setValues((current) => ({ ...current, isFavorite: event.target.checked }))} />
                </div>

                <FormGrid columns={2}>
                    {(edit || files.length <= 1) && <FieldInput {...field("title")} label="Título" placeholder="Ej: Manual de arquitectura" isRequired={edit} />}
                    <FieldInput {...field("author")} label="Autor" placeholder="Ej: Equipo de documentación" />
                    {edit && <FieldSelect {...field("groupId")} label="Grupo" options={groupOptions} placeholder="Sin grupo" isSearchable={groups.length >= 8} />}
                    <FieldTextarea {...field("description")} label="Descripción / notas" placeholder="Qué contiene, por qué lo guardé, versión..." rows={5} span={2} />
                </FormGrid>

                {edit && <div className="LStudio__library_file_identity"><FaFilePdf /><div><strong>{document.originalName}</strong><small>El archivo original no se reemplaza desde editar. Para cambiarlo, eliminá este PDF y subí el nuevo.</small></div></div>}
            </div>
        </AppModal>
    );
};
